import { useCallback } from "react";
import {
  useGenerateObjectiveImageMutation,
  useRegenerateObjectiveImageMutation,
  type GenerateObjectiveImageMutationVariables,
  type RegenerateObjectiveImageMutationVariables,
} from "@axio/graphql/generated/graphql";
import { type CompletedImageGenerationJob, useImageGenerationJobPolling } from "../useImageGenerationPolling";

export type CompletedObjectiveImageJob = CompletedImageGenerationJob;

type SyncImageMutationPayload = {
  data?: Array<{ id: string; url: string; description?: string | null }> | null;
  success?: boolean;
  message?: string | null;
  imageGenerationJobId?: string | null;
};

const completedImageFromMutationPayload = (
  payload: SyncImageMutationPayload | null | undefined
): CompletedObjectiveImageJob | null => {
  const first = payload?.data?.[0];
  if (!first?.url) return null;
  return {
    id: first.id,
    url: first.url,
    description: first.description ?? null,
  };
};

export const useObjectiveImageGeneration = () => {
  const [generateObjectiveImageMutation] = useGenerateObjectiveImageMutation();
  const [regenerateObjectiveImageMutation] = useRegenerateObjectiveImageMutation();
  const { waitForJob } = useImageGenerationJobPolling();

  const requestObjectiveImageGeneration = useCallback(
    async (variables: GenerateObjectiveImageMutationVariables): Promise<CompletedObjectiveImageJob | null> => {
      try {
        const response = await generateObjectiveImageMutation({ variables });
        const payload = response.data?.generateObjectiveImage;
        if (!payload?.success) {
          console.error("[ILE] generateObjectiveImage failed", { message: payload?.message });
          return null;
        }

        const syncResult = completedImageFromMutationPayload(payload);
        if (syncResult) return syncResult;

        const jobId = payload.imageGenerationJobId;
        if (!jobId) {
          console.error("[ILE] generateObjectiveImage missing imageGenerationJobId", { message: payload.message });
          return null;
        }
        return waitForJob(jobId, { logPrefix: "[ILE]" });
      } catch (error) {
        console.error("[ILE] generateObjectiveImage request failed", { error });
        return null;
      }
    },
    [generateObjectiveImageMutation, waitForJob]
  );

  const requestObjectiveImageRegeneration = useCallback(
    async (variables: RegenerateObjectiveImageMutationVariables): Promise<CompletedObjectiveImageJob | null> => {
      try {
        const response = await regenerateObjectiveImageMutation({ variables });
        const payload = response.data?.regenerateObjectiveImage;
        if (!payload?.success) {
          console.error("[ILE] regenerateObjectiveImage failed", { message: payload?.message });
          return null;
        }

        const syncResult = completedImageFromMutationPayload(payload);
        if (syncResult) return syncResult;

        const jobId = payload.imageGenerationJobId;
        if (!jobId) {
          console.error("[ILE] regenerateObjectiveImage missing imageGenerationJobId", { message: payload.message });
          return null;
        }
        return waitForJob(jobId, { logPrefix: "[ILE]" });
      } catch (error) {
        console.error("[ILE] regenerateObjectiveImage request failed", { error });
        return null;
      }
    },
    [regenerateObjectiveImageMutation, waitForJob]
  );

  return {
    requestObjectiveImageGeneration,
    requestObjectiveImageRegeneration,
  };
};

export default useObjectiveImageGeneration;
